<script>
import { resolveDefaultAuthenticatedRoute } from '@/utils/authRouting'

export default {
  name: 'UnifiedLoginBox',
  data () {
    return {
      email: '',
      password: '',
      showPassword: false,
      isSubmitting: false,
      errorMessage: ''
    }
  },
  methods: {
    validate () {
      if (!this.email.trim()) {
        this.errorMessage = '이메일을 입력해 주세요.'
        return false
      }
      if (!this.password) {
        this.errorMessage = '비밀번호를 입력해 주세요.'
        return false
      }
      return true
    },
    async signIn () {
      if (this.isSubmitting || !this.validate()) return

      this.isSubmitting = true
      this.errorMessage = ''

      try {
        const res = await this.$http({
          method: 'POST',
          url: '/api/auth/signIn',
          data: {
            email: this.email.trim(),
            pwd: this.password
          },
          name: '[ Axios / 통합 로그인 ]',
          skipSpinner: true
        })

        const userData = res.data
        this.$store.dispatch('setCustomerInfo', userData)

        if (Number(userData?.authLevel) >= 80) {
          this.$store.dispatch('loadParcel', [])
        }

        await this.$router.replace(resolveDefaultAuthenticatedRoute(userData))
      } catch (error) {
        this.$log.debug('/auth/signIn 실패', error)
        this.errorMessage = error?.response?.data?.message || '로그인할 수 없습니다. 잠시 후 다시 시도해 주세요.'
      } finally {
        this.isSubmitting = false
      }
    }
  },
  mounted () {
    if (this.$store.getters.getSessionStatus) {
      this.$router.replace(resolveDefaultAuthenticatedRoute(this.$store.getters.getLocalSession))
    }
  }
}
</script>

<template>
  <q-page class="unified-login-page row items-center justify-center q-pa-md">
    <q-card class="login-card">
      <div class="row no-wrap full-height">
        <section class="login-visual col-6 gt-sm">
          <div class="login-visual__overlay">
            <div class="login-visual__content">
              <div class="login-visual__eyebrow">AONE LOGISTICS</div>
              <h1>성공적인 비지니스 파트너 AONE</h1>
              <p>전자상거래 | 풀필먼트 서비스 | 항공/해상 | 창고보관</p>
            </div>
          </div>
        </section>

        <section class="login-form col-12 col-md-6">
          <img
            class="login-logo"
            :src="require('@/assets/imgs/common/logo.png')"
            alt="AONE Logistics">

          <div class="login-heading">
            <div class="text-overline text-primary">AONE ACCOUNT</div>
            <h2>로그인</h2>
            <p>가입한 이메일과 비밀번호를 입력해 주세요.</p>
          </div>

          <q-form
            class="q-gutter-md"
            @submit.prevent="signIn">
            <q-input
              v-model="email"
              outlined
              type="email"
              label="이메일"
              autocomplete="username"
              :disable="isSubmitting"
              @update:model-value="errorMessage = ''">
              <template #prepend>
                <q-icon name="mail_outline" />
              </template>
            </q-input>

            <q-input
              v-model="password"
              outlined
              :type="showPassword ? 'text' : 'password'"
              label="비밀번호"
              autocomplete="current-password"
              :disable="isSubmitting"
              @update:model-value="errorMessage = ''">
              <template #prepend>
                <q-icon name="lock_outline" />
              </template>
              <template #append>
                <q-icon
                  class="cursor-pointer"
                  :name="showPassword ? 'visibility_off' : 'visibility'"
                  @click="showPassword = !showPassword" />
              </template>
            </q-input>

            <div
              v-if="errorMessage"
              class="login-error"
              role="alert">
              <q-icon name="error_outline" />
              <span>{{ errorMessage }}</span>
            </div>

            <q-btn
              class="full-width login-submit"
              type="submit"
              color="primary"
              unelevated
              size="lg"
              label="로그인"
              :loading="isSubmitting"
              :disable="isSubmitting" />
          </q-form>

          <div class="login-links row items-center justify-center q-gutter-x-md">
            <q-btn flat no-caps color="grey-8" label="회원가입" to="/signUp" />
          </div>

          <!-- <p class="login-help">계정 유형을 선택할 필요 없이 자동으로 확인합니다.</p> -->
        </section>
      </div>
    </q-card>
  </q-page>
</template>

<style scoped>
.unified-login-page {
  min-height: calc(100vh - 100px);
  background:
    radial-gradient(circle at 12% 10%, rgba(55, 149, 210, 0.2), transparent 32%),
    linear-gradient(145deg, #eef5f9 0%, #f8fafc 55%, #e8f0f5 100%);
}

.login-card {
  width: min(920px, 100%);
  min-height: 570px;
  overflow: hidden;
  border-radius: 20px;
  box-shadow: 0 22px 60px rgba(16, 45, 66, 0.18);
}

.login-visual {
  min-height: 570px;
  background: url('~@/assets/imgs/background/login.png') center / cover no-repeat;
}

.login-visual__overlay {
  display: flex;
  height: 100%;
  align-items: flex-end;
  padding: 48px;
  color: white;
  background: linear-gradient(180deg, rgba(4, 27, 44, 0.12), rgba(4, 35, 56, 0.88));
}

.login-visual__eyebrow {
  margin-bottom: 12px;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.18em;
}

.login-visual h1 {
  margin: 0 0 16px;
  font-size: 2.25rem;
  font-weight: 700;
  line-height: 1.3;
}

.login-visual p {
  max-width: 330px;
  margin: 0;
  color: rgba(255, 255, 255, 0.84);
  font-size: 0.95rem;
  line-height: 1.7;
}

.login-form {
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 48px 52px;
  background: white;
}

.login-logo {
  width: 124px;
  height: auto;
  margin-bottom: 36px;
}

.login-heading h2 {
  margin: 2px 0 8px;
  color: #17384f;
  font-size: 2rem;
  font-weight: 700;
}

.login-heading p {
  margin: 0 0 28px;
  color: #647784;
}

.login-error {
  display: flex;
  gap: 8px;
  align-items: center;
  padding: 10px 12px;
  border-radius: 8px;
  color: #b3261e;
  background: #fff2f0;
  font-size: 0.875rem;
}

.login-submit {
  min-height: 50px;
  border-radius: 8px;
  font-weight: 700;
}

.login-links {
  margin-top: 20px;
}

.login-links :deep(.q-btn) {
  font-size: 0.875rem;
}

.login-help {
  margin: 20px 0 0;
  color: #82929c;
  font-size: 0.78rem;
  text-align: center;
}

@media (max-width: 1023px) {
  .unified-login-page {
    min-height: calc(100vh - 60px);
  }

  .login-card {
    max-width: 520px;
    min-height: auto;
  }
}

@media (max-width: 599px) {
  .unified-login-page {
    padding: 16px;
  }

  .login-card {
    border-radius: 14px;
  }

  .login-form {
    padding: 36px 24px;
  }

  .login-logo {
    margin-bottom: 28px;
  }
}
</style>
